import { useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { useAuthStore } from '@/stores/auth'
import { useUIStore } from '@/stores/ui'
import { useIsInternalAdmin } from '@/hooks/useTrialStatus'
import { GET_SELECTED_TEAM_ADMINS, GET_TEAM_ADMIN_INVITE_LINKS } from '@/graphql/queries/team'
import { GENERATE_TEAM_ADMIN_INVITE_LINK, REVOKE_TEAM_ADMIN_INVITE_LINK } from '@/graphql/mutations/teams'
import { formatDate, copyToClipboard, classNames } from '@/lib/utils'
import { Button, Dialog, DialogHeader, DialogBody, DialogFooter } from '@/components/ui'
import {
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableEmpty,
  TableRowsSkeleton,
} from '@/components/Table'
import { UserAvatar } from '@/components/UserAvatar'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/Select'

// Mirrors the backend's TeamAdminRoleOptions enum (admin/reviewer/internal/salesRep/marketingLead)
// — not imported from generated/graphql.ts since that file isn't part of this app's build graph
// today (nothing else imports from it) and has pre-existing duplicate type declarations that only
// surface once something actually references it.
type TeamAdminRoleOptions = 'admin' | 'reviewer' | 'internal' | 'salesRep' | 'marketingLead'

// Sentinel for the expiration Select — Radix Select.Item requires a non-empty string value, so
// "never expires" is encoded as this string in the Select and translated to NEVER_EXPIRES_DAYS
// right before the mutation call. No backend change needed for this: expiresInDays already
// accepts any integer, so a 100-year value is indistinguishable from "never" in practice without
// requiring a nullable-schema migration.
const NEVER_EXPIRES_VALUE = 'never'
const NEVER_EXPIRES_DAYS = 36500

// Only shown to internal admins (see isInternalAdmin below) — a regular customer admin's invite
// links always grant 'admin', same as before this feature existed. The backend independently
// enforces this restriction (BAC-1834), so this is a UX convenience, not the security boundary.
const ROLE_OPTIONS: { value: TeamAdminRoleOptions; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'reviewer', label: 'Reviewer' },
  { value: 'internal', label: 'Internal' },
  { value: 'salesRep', label: 'Sales Rep' },
  { value: 'marketingLead', label: 'Marketing Lead' },
]
const ROLE_LABELS = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.value, r.label])) as Record<
  TeamAdminRoleOptions,
  string
>

export function AccountAdminsModal({ onClose }: { onClose: () => void }) {
  const selectedTeamId = useAuthStore((s) => s.selectedTeamId)
  const addToast = useUIStore((s) => s.addToast)
  const isInternalAdmin = useIsInternalAdmin()
  const [adminView, setAdminView] = useState<'list' | 'inviteLink'>('list')
  const [linkExpiresInDays, setLinkExpiresInDays] = useState(30)
  const [linkRole, setLinkRole] = useState<TeamAdminRoleOptions>('admin')

  const { data: adminsData, loading: adminsLoading } = useQuery(GET_SELECTED_TEAM_ADMINS, {
    variables: { teamId: selectedTeamId! },
    skip: !selectedTeamId,
  })

  const { data: inviteLinksData, refetch: refetchInviteLinks } = useQuery(GET_TEAM_ADMIN_INVITE_LINKS, {
    variables: { teamId: selectedTeamId! },
    skip: !selectedTeamId || adminView !== 'inviteLink',
  })

  const [generateInviteLink, { loading: generatingLink }] = useMutation(GENERATE_TEAM_ADMIN_INVITE_LINK, {
    onCompleted: () => {
      addToast({ type: 'success', message: 'Invite link created' })
      refetchInviteLinks()
    },
    onError: () => addToast({ type: 'error', message: 'Failed to create invite link' }),
  })

  const [revokeInviteLink] = useMutation(REVOKE_TEAM_ADMIN_INVITE_LINK, {
    onCompleted: () => {
      addToast({ type: 'success', message: 'Invite link revoked' })
      refetchInviteLinks()
    },
    onError: () => addToast({ type: 'error', message: 'Failed to revoke invite link' }),
  })

  // Internal admins are Rolla staff managing this team on the customer's behalf, not the
  // customer's own admins — don't show them in the customer-facing Account Admins list.
  const admins = [...(adminsData?.getTeamAdmins?.rows ?? [])]
    .filter((admin: Record<string, unknown>) => admin.role !== 'internal' && admin.role !== 'salesRep')
    .sort((a: Record<string, unknown>, b: Record<string, unknown>) => {
      const aCreatedAt = (a.profile as Record<string, unknown>)?.createdAt as string
      const bCreatedAt = (b.profile as Record<string, unknown>)?.createdAt as string
      return new Date(bCreatedAt).getTime() - new Date(aCreatedAt).getTime()
    })
  const inviteLinks = inviteLinksData?.getTeamAdminInviteLinks ?? []

  return (
    <Dialog onClose={onClose}>
      <DialogHeader>
        <span className="flex items-center justify-between w-full">
          Account Admins
          {adminView === 'list' && (
            <Button size="sm" onClick={() => setAdminView('inviteLink')}>
              Invite
            </Button>
          )}
        </span>
      </DialogHeader>

      {adminView === 'inviteLink' ? (
        <DialogBody className="space-y-4">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="font-semibold text-gray-900">Generate a link</p>
            <p className="mt-1 text-sm text-gray-500">
              Anyone who signs up or signs in through this link automatically becomes{' '}
              {isInternalAdmin ? 'a team admin with the role selected below' : 'an admin'} — share it only with
              people you want to grant access to.
            </p>
            {isInternalAdmin && (
              <div className="mt-3">
                <Select value={linkRole} onValueChange={(v) => setLinkRole(v as TeamAdminRoleOptions)}>
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="mt-3 flex items-center gap-2">
              <Select
                value={linkExpiresInDays === NEVER_EXPIRES_DAYS ? NEVER_EXPIRES_VALUE : String(linkExpiresInDays)}
                onValueChange={(v) => setLinkExpiresInDays(v === NEVER_EXPIRES_VALUE ? NEVER_EXPIRES_DAYS : Number(v))}
              >
                <SelectTrigger className="h-10 flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Expires in 1 day</SelectItem>
                  <SelectItem value="7">Expires in 7 days</SelectItem>
                  <SelectItem value="14">Expires in 14 days</SelectItem>
                  <SelectItem value="30">Expires in 30 days</SelectItem>
                  <SelectItem value="90">Expires in 90 days</SelectItem>
                  <SelectItem value={NEVER_EXPIRES_VALUE}>Never expires</SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={() =>
                  generateInviteLink({
                    variables: {
                      teamId: selectedTeamId,
                      expiresInDays: linkExpiresInDays,
                      role: isInternalAdmin ? linkRole : 'admin',
                    },
                  })
                }
                disabled={generatingLink}
                className="h-10 flex-shrink-0"
              >
                {generatingLink ? 'Generating...' : 'Generate Link'}
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            {inviteLinks.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-500">No invite links yet.</p>
            ) : (
              inviteLinks.map((link: Record<string, unknown>) => {
                const expiresAt = link.expiresAt as string
                // A link generated with the "Never expires" option is given a ~100-year
                // expiresAt rather than a null value (see NEVER_EXPIRES_DAYS) — anything more
                // than ~50 years out is treated as effectively non-expiring for display purposes.
                const neverExpires = new Date(expiresAt).getFullYear() - new Date().getFullYear() > 50
                const expired = !neverExpires && new Date(expiresAt) < new Date()
                const revoked = link.revoked as boolean
                const inactive = revoked || expired
                const role = (link.role as TeamAdminRoleOptions) ?? 'admin'
                const url = `${window.location.origin}/login?invite=${link.code as string}`
                return (
                  <div
                    key={link.id as string}
                    className={classNames('rounded-xl border border-gray-200 bg-gray-50 p-3', inactive && 'opacity-60')}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="flex items-center text-xs text-gray-500">
                        {revoked ? 'Revoked' : expired ? 'Expired' : neverExpires ? 'Never expires' : `Expires ${formatDate(expiresAt)}`}
                        {' · '}
                        {link.redemptionCount as number} redemption
                        {(link.redemptionCount as number) === 1 ? '' : 's'}
                        {role !== 'admin' && (
                          <span className="ml-2 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
                            {ROLE_LABELS[role]}
                          </span>
                        )}
                      </p>
                      {!revoked && (
                        <button
                          onClick={() => revokeInviteLink({ variables: { id: link.id } })}
                          className="text-xs font-semibold text-red-600 hover:text-red-700"
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3">
                        <svg
                          className="h-4 w-4 flex-shrink-0 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5"
                          />
                        </svg>
                        <span className="truncate text-sm text-gray-700">{url}</span>
                      </div>
                      <button
                        onClick={async () => {
                          await copyToClipboard(url)
                          addToast({ type: 'success', message: 'Link copied!' })
                        }}
                        disabled={inactive}
                        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-500 transition hover:shadow disabled:cursor-not-allowed disabled:opacity-40"
                        title="Copy"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      </button>
                      <a
                        href={inactive ? undefined : url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-disabled={inactive}
                        className={classNames(
                          'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-500 transition',
                          inactive ? 'pointer-events-none opacity-40' : 'hover:shadow',
                        )}
                        title="Open in new tab"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                    </div>
                  </div>
                )
              })
            )}
          </div>

          <Button variant="secondary" onClick={() => setAdminView('list')} className="w-full">
            Back
          </Button>
        </DialogBody>
      ) : (
        <DialogBody className="max-h-96 overflow-y-auto !p-0">
          <table className="w-full">
            <TableHead>
              <TableHeaderCell>Joined</TableHeaderCell>
              <TableHeaderCell>User</TableHeaderCell>
            </TableHead>
            <TableBody>
              {adminsLoading ? (
                <TableRowsSkeleton rows={5} columns={2} avatarColumn={1} avatarSize="sm" />
              ) : admins.length === 0 ? (
                <TableEmpty colSpan={2}>No admins yet</TableEmpty>
              ) : (
                admins.map((admin: Record<string, unknown>) => {
                  const p = admin.profile as Record<string, unknown>
                  const emails = (p.emailAddresses ?? []) as Array<{ emailAddress: string; primary: boolean }>
                  const email = (emails.find((e) => e.primary) ?? emails[0])?.emailAddress ?? ''
                  return (
                    <TableRow key={admin.id as string}>
                      <TableCell className="text-gray-500">{formatDate(p.createdAt as string)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <UserAvatar
                            profile={{
                              firstName: p.firstName as string | null,
                              lastName: p.lastName as string | null,
                              photo: p.photo as string | null,
                              emailAddresses: p.emailAddresses as Array<{
                                emailAddress: string
                                primary?: boolean
                              }> | null,
                            }}
                            size="sm"
                          />
                          <div>
                            <p className="font-medium text-gray-900">
                              {p.firstName as string} {p.lastName as string}
                            </p>
                            <p className="text-xs text-gray-500">{email}</p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </table>
        </DialogBody>
      )}

      <DialogFooter>
        <Button variant="ghost" onClick={onClose} className="w-full">
          Close
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
